/**
 * Default Voyager API configuration and derived URL helpers.
 * Extracted from VoyagerSearchService.ts.
 */

import type { VoyagerApiConfig } from './VoyagerSearchService.types';
import { environment } from 'nsdi-volto-frontend/config/environments';

export const DEFAULT_VOYAGER_CONFIG: VoyagerApiConfig = {
  baseUrl: environment.voyager.baseUrl,
  configId: environment.voyager.configId,
  facetConfig: {
    fields: [
      { field: '{!ex=grp_NSDI}grp_NSDI', label: 'NSDI' },
      { field: '{!ex=grp_NSDI_Sector}grp_NSDI_Sector', label: 'NSDI Sector' },
      {
        field: '{!ex=grp_NSDI_Organization}grp_NSDI_Organization',
        label: 'NSDI Organization',
      },
      { field: '{!ex=grp_NSDI_Agency}grp_NSDI_Agency', label: 'NSDI Agency' },
      {
        field: '{!ex=grp_NSDI_Program}grp_NSDI_Program',
        label: 'NSDI Program',
      },
      { field: 'tree_nsdi', label: 'Tree NSDI' },
      {
        field: '{!ex=fs_nsdi_classification}fs_nsdi_classification',
        label: 'NSDI Classification',
      },
      {
        field: '{!ex=format_category}format_category',
        label: 'Format Category',
      },
      { field: 'format', label: 'Format' },
      { field: 'format_type', label: 'Format Type' },
      { field: 'format_keyword', label: 'Format Keyword' },
      { field: 'geometry_type', label: 'Geometry Type' },
      { field: 'keywords', label: 'Keywords' },
      { field: 'properties', label: 'Properties' },
      { field: 'author', label: 'Author' },
      { field: 'srs', label: 'Spatial Reference' },
      { field: 'fileExtension', label: 'File Extension' },
      { field: '{!ex=location}location', label: 'Location' },
    ],
    params: {
      'tree_nsdi.facet.sort': 'index',
      'f.tree_nsdi.facet.sort': 'index',
    },
    limit: 11,
    minCount: 1,
  },
};

export const DEFAULT_VOYAGER_FACET_FIELDS =
  DEFAULT_VOYAGER_CONFIG.facetConfig.fields;

export const DEFAULT_VOYAGER_FACET_PARAMS =
  DEFAULT_VOYAGER_CONFIG.facetConfig.params || {};

/** Derives the Solr search endpoint URL from a config object. */
/**
 * Normalise a configured Voyager base URL.
 *
 * A host entered without a scheme ('sit-vs-doi.dev-gpgov.chs.usgs.gov') is a
 * RELATIVE path to the browser, so it resolves against the Volto origin and the
 * request 404s on the frontend instead of reaching Voyager. Editors type the
 * host into a sidebar field, so assume https rather than fail obscurely.
 */
export const normalizeVoyagerBaseUrl = (raw: string | undefined): string => {
  const trimmed = (raw ?? '').trim().replace(/\/$/, '');
  if (!trimmed) return trimmed;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const getVoyagerSearchUrl = (config: VoyagerApiConfig): string => {
  const baseUrl = normalizeVoyagerBaseUrl(config.baseUrl);
  if (baseUrl.endsWith('/solr/v0/select')) {
    return baseUrl;
  }
  return `${baseUrl}/solr/v0/select`;
};

/** Derives the display-config REST endpoint URL from a config object. */
export const getVoyagerDisplayConfigUrl = (
  config: VoyagerApiConfig,
): string => {
  const baseUrl = normalizeVoyagerBaseUrl(config.baseUrl).replace(
    /\/solr\/v0\/select$/,
    '',
  );
  return `${baseUrl}/api/rest/display/config`;
};
